import Typography from "@mui/material/Typography";
import { parseEvents, useEvents } from "api/events";
import { AttributeChips } from "components/Table/AttributeChips";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_CUSTOM_EVENTS_LIST_ID } from "state/tables/types";
import { ListingActions, ListResponse } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { useStyles } from "./SingleMember.styles";

type CustomEventItem = {
  body: {};
  createdAt?: string | Date;
  customEventId: string;
  eventDate?: string | Date;
  eventName: string;
};

function createItems(items: CustomEventItem[]): Row[] {
  return items.map((item: CustomEventItem) => ({
    cells: [
      { content: OLDate(item.eventDate), property: "eventDate" },
      { content: item.eventName, property: "eventName" },
      {
        content: (
          <>
            {Object.keys(item.body)?.length ? (
              <AttributeChips items={parseEvents(item.body)} />
            ) : (
              "-"
            )}
          </>
        ),
        property: "eventAttributes",
      },
      { content: OLDate(item.createdAt), property: "createdAt" },
    ],
    id: item.customEventId,
    testId: "events-table-row",
  }));
}

export function CustomEvents() {
  const { id: memberID } = useParams<{ id: string; page: string }>();
  const { message } = useMessage();
  const classes = useStyles();
  const { useListOfCustomEvents } = useEvents();
  const { listing, updateListing } = useListing(USER_CUSTOM_EVENTS_LIST_ID);
  const navigate = useNavigate();

  const { data: listOfCustomEvents } = useListOfCustomEvents({
    filters: memberID
      ? [
          {
            condition: "eq",
            query: "customerId",
            value: memberID,
          },
        ]
      : undefined,
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "eventDate",
  }) as { data: ListResponse };
  const { setColumnOrdering } = useTablesStateHelpers();

  const headItems = createHeadItems([
    {
      headerText: message({ id: "customEventDate" }),
      property: "eventDate",
      isSortable: true,
    },
    {
      headerText: message({ id: "customEventName" }),
      property: "eventName",
      isSortable: true,
    },
    {
      headerText: message({ id: "customEventAttributes" }),
      property: "eventAttributes",
      isSortable: false,
    },
    {
      headerText: message({ id: "createdOn" }),
      isSortable: true,
      isVisible: false,
      property: "createdAt",
    },
  ]);

  const items = listOfCustomEvents?.items
    ? createItems(listOfCustomEvents.items)
    : [];

  return (
    <>
      <Typography
        mb={6}
        data-test-id="single-member-events-title"
        className={classes.header}
      >
        {message({ id: "customEvents" })}
      </Typography>
      <OLTable
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) =>
                navigate(links.customEvent(`${id}`), {
                  state: { prevPath: links.singleMemberCustomEvents(memberID) },
                }),
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        columns={listOfCustomEvents && headItems}
        data-test-id="single-member-events"
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            listOfCustomEvents?.total.filtered,
            listOfCustomEvents?.total.all,
          ),
          onChangePage: (page) => {
            updateListing({
              payload: page,
              type: ListingActions.CHANGE_PAGE,
            });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={listOfCustomEvents && items}
        setColumnOrdering={setColumnOrdering}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "eventDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        tableName={USER_CUSTOM_EVENTS_LIST_ID}
      />
    </>
  );
}
